import { Component } from '@theme/component';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';
import { fetchConfig } from '@theme/utilities';

/*
 * Cart conversion helpers loaded with the cart drawer (snippets/cart-drawer-inner.liquid) and the
 * mobile "Sepetin hazır" bar (sections/mobile-bottom-nav.liquid):
 * - <swist-cart-recs>: "Bununla iyi gider" cards with one-tap add, under the 2nd-item progress box
 * - <swist-cart-delivery>: "Bugün kargoda · Tahmini teslim 23–27 Ekim" above the drawer total
 * - <swist-cart-save>: "Sepetimi kaydet" dialog (WhatsApp to self, copy link, optional e-mail sign-up)
 * - <swist-cart-ready-bar>: fixed mobile bar with the cart count, total and a checkout link
 */

/** Recommendation HTML per product ID, so drawer re-renders do not refetch. @type {Map<string, string>} */
const recsCache = new Map();

/** @returns {string[]} Section IDs of the cart components on the page, for the add response. */
function cartSectionIds() {
  const ids = [];
  for (const element of document.querySelectorAll('cart-items-component')) {
    if (element instanceof HTMLElement && element.dataset.sectionId) ids.push(element.dataset.sectionId);
  }
  return ids;
}

/** @returns {Promise<{ item_count: number, total_price: number, currency: string, items: Array<{ variant_id: number, quantity: number }> }>} */
async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
  return response.json();
}

/**
 * Drawer recommendations. The list keeps its cards across drawer re-renders
 * (data-skip-subtree-update); cards for products that reach the cart are removed.
 *
 * @typedef {{ list: HTMLElement, status: HTMLElement }} RecsRefs
 * @extends {Component<RecsRefs>}
 */
class SwistCartRecs extends Component {
  requiredRefs = ['list', 'status'];

  /** @type {AbortController | undefined} */
  #listeners;
  /** @type {AbortController | undefined} */
  #request;
  #loadedFor = '';

  connectedCallback() {
    super.connectedCallback();
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    this.addEventListener('click', this.handleClick, { signal: this.#listeners.signal });
    void this.#load();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#prune();
    void this.#load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#request?.abort();
  }

  /** @param {MouseEvent} event */
  handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const opener = event.target.closest('[data-recs-open]');
    if (opener instanceof HTMLButtonElement) {
      this.#openSizes(opener);
      return;
    }

    const adder = event.target.closest('[data-recs-add]');
    if (adder instanceof HTMLButtonElement && !adder.disabled) void this.#add(adder);
  };

  async #load() {
    const productId = this.dataset.productId ?? '';
    if (!productId || productId === this.#loadedFor) return;

    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;

    let html = recsCache.get(productId);
    if (html === undefined) {
      try {
        const url = new URL(this.dataset.url || '/recommendations/products', window.location.origin);
        url.searchParams.set('product_id', productId);
        url.searchParams.set('limit', '10');
        url.searchParams.set('intent', 'related');
        url.searchParams.set('section_id', 'swist-cart-recs');
        const response = await fetch(url, { signal: request.signal });
        if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        html = doc.querySelector('[data-recs-source]')?.innerHTML.trim() ?? '';
        recsCache.set(productId, html);
      } catch (error) {
        if (request.signal.aborted) return;
        console.error(error);
        html = '';
      }
    }

    if (request !== this.#request) return;
    this.#loadedFor = productId;
    this.refs.list.innerHTML = html;
    this.#prune();
  }

  /** Drops cards whose product is now in the cart; CSS hides the block when no card is left. */
  #prune() {
    const inCart = new Set((this.dataset.cartProductIds ?? '').split(',').filter(Boolean));
    for (const card of this.refs.list.querySelectorAll('[data-product-id]')) {
      if (card instanceof HTMLElement && inCart.has(card.dataset.productId ?? '')) card.remove();
    }
  }

  /** @param {HTMLButtonElement} opener */
  #openSizes(opener) {
    const sizes = document.getElementById(opener.getAttribute('aria-controls') ?? '');
    if (!sizes) return;
    opener.setAttribute('aria-expanded', 'true');
    opener.hidden = true;
    sizes.hidden = false;
    const firstChip = sizes.querySelector('button:not([disabled])');
    if (firstChip instanceof HTMLElement) firstChip.focus();
  }

  /** @param {HTMLButtonElement} button */
  async #add(button) {
    const variantId = button.dataset.variantId;
    const card = button.closest('[data-product-id]');
    if (!variantId || !(card instanceof HTMLElement)) return;

    const buttons = card.querySelectorAll('button');
    for (const element of buttons) element.setAttribute('aria-disabled', 'true');
    card.classList.add('is-adding');
    button.setAttribute('aria-busy', 'true');
    this.refs.status.textContent = this.dataset.addingText ?? '';

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections: cartSectionIds().join(','),
            sections_url: window.location.pathname,
          }),
        })
      );
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || '');

      this.refs.status.textContent = this.dataset.addedText ?? '';
      // Outside the product form, the header cart icon reads itemCount as the new cart total
      const itemCount = (Number(this.dataset.cartCount) || 0) + 1;
      this.dataset.cartCount = String(itemCount);
      this.dispatchEvent(
        new CartAddEvent({}, variantId, {
          source: 'swist-cart-recs',
          itemCount,
          productId: card.dataset.productId,
          variantId,
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error(error);
      card.classList.remove('is-adding');
      for (const element of buttons) element.removeAttribute('aria-disabled');
      this.refs.status.textContent =
        (error instanceof Error && error.message) || this.dataset.errorText || '';
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

/**
 * Compact delivery line. Follows the store's stated rule (product page "Kargo & Teslimat"):
 * Monday–Friday before the cutoff hour ship the same day, later orders and weekends the next
 * business day; delivery takes data-min…data-max business days after dispatch.
 * Times use Istanbul time (UTC+3). data-manual replaces the line, e.g. during holidays.
 */
class SwistCartDelivery extends HTMLElement {
  #timer = 0;

  connectedCallback() {
    this.render();
    window.clearInterval(this.#timer);
    this.#timer = window.setInterval(() => this.render(), 60000);
  }

  disconnectedCallback() {
    window.clearInterval(this.#timer);
  }

  /** Current time shifted so its local fields read as Istanbul time (UTC+3, no DST). */
  static now() {
    const date = new Date();
    return new Date(date.getTime() + (date.getTimezoneOffset() + 180) * 60000);
  }

  /** @param {Date} date */
  static iso(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  get #holidays() {
    return new Set(
      (this.dataset.holidays ?? '')
        .split(/[\s,]+/)
        .map((value) => value.trim())
        .filter(Boolean)
    );
  }

  /** @param {Date} date */
  #isBusinessDay(date) {
    const day = date.getDay();
    return day !== 0 && day !== 6 && !this.#holidays.has(SwistCartDelivery.iso(date));
  }

  /**
   * @param {Date} start
   * @param {number} count
   */
  #addBusinessDays(start, count) {
    const date = new Date(start);
    let added = 0;
    while (added < count) {
      date.setDate(date.getDate() + 1);
      if (this.#isBusinessDay(date)) added += 1;
    }
    return date;
  }

  render() {
    const line = this.querySelector('[data-delivery-line]');
    const estimate = this.querySelector('[data-delivery-estimate]');
    if (!(line instanceof HTMLElement) || !(estimate instanceof HTMLElement)) return;

    const { manual = '' } = this.dataset;
    if (manual) {
      const text = document.createElement('strong');
      text.textContent = manual;
      line.replaceChildren(text);
      estimate.hidden = true;
      return;
    }

    const now = SwistCartDelivery.now();
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);

    const cutoff = Number(this.dataset.cutoff) || 12;
    const dispatch = this.#isBusinessDay(today) && now.getHours() < cutoff ? today : this.#addBusinessDays(today, 1);

    const locale = document.documentElement.lang || 'tr';
    const daysAway = Math.round((dispatch.getTime() - today.getTime()) / 86400000);
    let dayText = new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(dispatch);
    if (daysAway === 0) dayText = this.dataset.today ?? dayText;
    if (daysAway === 1) dayText = this.dataset.tomorrow ?? dayText;

    const strong = document.createElement('strong');
    strong.textContent = (this.dataset.dispatch ?? '[day]').replace('[day]', dayText);
    line.replaceChildren(strong);

    const min = Number(this.dataset.min) || 1;
    const max = Math.max(min, Number(this.dataset.max) || min);
    const from = this.#addBusinessDays(dispatch, min);
    const to = this.#addBusinessDays(dispatch, max);
    const dayMonth = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' });
    let range = dayMonth.format(to);
    if (from.getTime() !== to.getTime()) {
      range =
        from.getMonth() === to.getMonth()
          ? `${from.getDate()}–${dayMonth.format(to)}`
          : `${dayMonth.format(from)} – ${dayMonth.format(to)}`;
    }
    estimate.textContent = (this.dataset.estimate ?? '[range]').replace('[range]', range);
    estimate.hidden = false;
  }
}

/**
 * "Sepetimi kaydet": builds a cart permalink that restores the cart in any browser (e.g. when an
 * Instagram in-app visitor later opens the shop in Safari) and offers it via WhatsApp or copy.
 * The optional e-mail form is Shopify's customer (newsletter) form, tagged "sepet-kaydet". It is
 * submitted natively so Shopify's invisible hCaptcha can attach its token; after the redirect back
 * (?customer_posted=true) the drawer and dialog reopen with the result rendered by Liquid.
 * Each use is stored in the cart attribute "_sepet_kaydet" so orders show whether it helped.
 *
 * @typedef {{
 *   dialog: HTMLDialogElement,
 *   whatsapp: HTMLAnchorElement,
 *   status: HTMLElement
 * }} SaveRefs
 * @extends {Component<SaveRefs>}
 */
class SwistCartSave extends Component {
  requiredRefs = ['dialog', 'whatsapp', 'status'];

  /** @type {HTMLElement | null} */
  #opener = null;
  #link = '';
  /** @type {AbortController | undefined} */
  #listeners;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    // "close" does not bubble and is not a framework-routed event; capture it on the host.
    this.addEventListener('close', this.#handleClose, { capture: true, signal: this.#listeners.signal });
    // Shopify's {% form %} tag cannot carry on:submit, so the sign-up form is wired here.
    this.addEventListener('submit', this.#subscribe, { signal: this.#listeners.signal });
    // Native listener on purpose: framework-routed on:click rewrites event.target to the element
    // carrying the attribute, so every click inside the dialog looked like a backdrop click.
    this.addEventListener('click', this.#handleBackdrop, { signal: this.#listeners.signal });
    const form = this.querySelector('form:has([data-form-state])');
    if (form && form.id && window.location.hash === `#${form.id}`) void this.#reopenAfterPost();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
  }

  /** Opens the dialog with a fresh link. @param {MouseEvent} event */
  async open(event) {
    this.#opener = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    const formStatus = this.querySelector('[data-form-status]');
    if (formStatus) formStatus.textContent = '';
    await this.#show();
  }

  async #show() {
    this.refs.status.textContent = '';
    try {
      const cart = await fetchCart();
      if (!cart.item_count) return;
      this.#link = this.#permalink(cart.items, 'whatsapp');
      const message = `${this.dataset.whatsappText ?? ''} ${this.#link}`.trim();
      this.refs.whatsapp.href = `https://wa.me/?text=${encodeURIComponent(message)}`;
    } catch (error) {
      console.error(error);
      return;
    }
    this.refs.dialog.showModal();
  }

  close() {
    this.refs.dialog.close();
  }

  /** Shows the sign-up result once after Shopify redirects back from the form post. */
  async #reopenAfterPost() {
    const url = new URL(window.location.href);
    url.searchParams.delete('customer_posted');
    url.hash = '';
    history.replaceState(history.state, '', url);

    if (document.readyState === 'loading') {
      await new Promise((resolve) => document.addEventListener('DOMContentLoaded', resolve, { once: true }));
    }
    const drawer = this.closest('cart-drawer-component');
    if (drawer) {
      await customElements.whenDefined('cart-drawer-component');
      if (!(drawer instanceof HTMLElement) || !('open' in drawer) || typeof drawer.open !== 'function') return;
      // The drawer opens on the next frame; wait so this dialog lands above it in the top layer.
      const opened = new Promise((resolve) => {
        drawer.addEventListener('dialog:open', resolve, { once: true });
        setTimeout(resolve, 1000);
      });
      drawer.open();
      await opened;
    }
    if (this.querySelector('[data-form-state="done"]')) this.#remember('eposta');
    await this.#show();
  }

  /** Restores focus to the "Sepetimi kaydet" button after the dialog closes. @param {Event} event */
  #handleClose = (event) => {
    if (event.target === this.refs.dialog) this.#opener?.focus();
  };

  /** Closes on a click on the backdrop (the dialog element itself, outside its panel). @param {MouseEvent} event */
  #handleBackdrop = (event) => {
    if (event.target === this.refs.dialog) this.close();
  };

  handleWhatsapp() {
    this.#remember('whatsapp');
  }

  async copy() {
    const link = this.#link.replace('utm_medium=whatsapp', 'utm_medium=kopya');
    try {
      await navigator.clipboard.writeText(link);
      this.refs.status.textContent = this.dataset.copiedText ?? '';
    } catch {
      window.prompt(this.dataset.copyPrompt ?? '', link);
    }
    this.#remember('kopya');
  }

  /**
   * Lets the form submit natively: Shopify's captcha script intercepts native submits to add an
   * invisible hCaptcha token, while a fetch() post has none and always lands on the /challenge page.
   * @param {SubmitEvent} event
   */
  #subscribe = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('swist-cart-save__form')) return;
    if (!form.checkValidity()) {
      event.preventDefault();
      form.reportValidity();
      return;
    }
    // Modal dialogs make the rest of the page inert, which stalls hCaptcha and hides any challenge
    // it shows; close both so the captcha can finish. The page reopens them after the redirect.
    this.close();
    const drawer = this.closest('cart-drawer-component');
    if (drawer && 'close' in drawer && typeof drawer.close === 'function') drawer.close();
  };

  /**
   * @param {Array<{ variant_id: number, quantity: number }>} items
   * @param {string} medium
   */
  #permalink(items, medium) {
    const lines = items.map((item) => `${item.variant_id}:${item.quantity}`).join(',');
    const url = new URL(`/cart/${lines}`, window.location.origin);
    url.searchParams.set('storefront', 'true');
    url.searchParams.set('utm_source', 'sepet-kaydet');
    url.searchParams.set('utm_medium', medium);
    return url.toString();
  }

  /** @param {string} channel */
  #remember(channel) {
    void fetch(
      `${Theme.routes.cart_update_url}.js`,
      fetchConfig('json', { body: JSON.stringify({ attributes: { _sepet_kaydet: channel } }) })
    ).catch((error) => console.error(error));
  }
}

const READY_BAR_KEY = 'swist-cart-ready-bar';

/**
 * Fixed mobile bar shown on browsing pages while the cart has items: "Sepetinde 2 ürün var ·
 * 2.068,00 TL · Ödemeye geç". Closing it hides it for the session until the cart grows again.
 *
 * @typedef {{ label: HTMLElement, total: HTMLElement }} BarRefs
 * @extends {Component<BarRefs>}
 */
class SwistCartReadyBar extends Component {
  requiredRefs = ['label', 'total'];

  /** @type {AbortController | undefined} */
  #listeners;
  /** @type {AbortController | undefined} */
  #request;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal: this.#listeners.signal });
    this.#show(Number(this.dataset.count) || 0);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#request?.abort();
    document.documentElement.classList.remove('swist-crb-on');
  }

  openCart() {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && 'open' in drawer && typeof drawer.open === 'function') {
      drawer.open();
    } else {
      window.location.href = Theme.routes.cart_url;
    }
  }

  dismiss() {
    try {
      sessionStorage.setItem(READY_BAR_KEY, String(Number(this.dataset.count) || 0));
    } catch {
      // Storage can be unavailable (private mode); the bar then only hides for this page.
    }
    this.#setVisible(false);
  }

  #handleCartUpdate = async () => {
    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        signal: request.signal,
      });
      const cart = await response.json();
      if (request !== this.#request) return;
      this.dataset.count = String(cart.item_count);
      this.refs.label.textContent = (
        cart.item_count === 1 ? this.dataset.labelOne : this.dataset.labelOther
      )?.replace('[count]', String(cart.item_count)) ?? '';
      this.refs.total.textContent = formatMoney(
        cart.total_price,
        this.dataset.moneyFormat ?? '{{amount}}',
        cart.currency
      );
      this.#show(cart.item_count);
    } catch (error) {
      if (!request.signal.aborted) console.error(error);
    }
  };

  /** @param {number} count */
  #show(count) {
    let dismissedAt = -1;
    try {
      dismissedAt = Number(sessionStorage.getItem(READY_BAR_KEY) ?? -1);
    } catch {
      dismissedAt = -1;
    }
    this.#setVisible(count > 0 && count > dismissedAt);
  }

  /** @param {boolean} visible */
  #setVisible(visible) {
    this.hidden = !visible;
    document.documentElement.classList.toggle('swist-crb-on', visible);
  }
}

if (!customElements.get('swist-cart-recs')) customElements.define('swist-cart-recs', SwistCartRecs);
if (!customElements.get('swist-cart-delivery')) customElements.define('swist-cart-delivery', SwistCartDelivery);
if (!customElements.get('swist-cart-save')) customElements.define('swist-cart-save', SwistCartSave);
if (!customElements.get('swist-cart-ready-bar')) customElements.define('swist-cart-ready-bar', SwistCartReadyBar);

/*
 * "Ürün seç" in the drawer's %10 box scrolls to the "Bununla iyi gider" cards instead of leaving
 * the page. Without cards (or without JavaScript) the link opens its href (new arrivals).
 */
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const link = event.target.closest('[data-swist-scroll-to]');
  if (!(link instanceof HTMLAnchorElement)) return;

  const target = document.getElementById(link.dataset.swistScrollTo ?? '');
  const firstCard = target?.querySelector('.swist-recs__card');
  if (!target || !firstCard) return;

  event.preventDefault();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  const firstAction = firstCard.querySelector('button');
  if (firstAction instanceof HTMLElement) firstAction.focus({ preventScroll: true });
});
